import { trackWithExtra } from '../../../lib/tracking'
import { colors } from '../../colors'
import { BargainUserInfo } from '../../types'

interface Chain {
  id: number
  name: string
  logo_url?: string | null
}

interface FilterBarProps {
  filterType: 'all' | 'following' | 'want_to_eat' | 'themes' | string
  isLoadingFilteredPosts: boolean
  bargain_user_info: BargainUserInfo
  followed_chains?: Chain[]
  selectedChain: string | null
  currentPage: 'posts' | 'themes'
  onFilterTypeChange?: (type: 'all' | 'following' | 'want_to_eat' | 'themes') => void
  onChainFilterChange?: (chainId: string) => void
}

const styles: Record<string, React.CSSProperties> = {
  filterButtons: {
    display: 'flex',
    gap: '8px',
    padding: '8px 14px',
    position: 'sticky',
    top: 0,
    backgroundColor: '#FFFFFF',
    zIndex: 10,
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    WebkitOverflowScrolling: 'touch',
  },
  filterButton: {
    width: 'auto',
    minWidth: 'fit-content',
    height: '40px',
    borderRadius: '20px',
    border: '1px solid #00000018',
    padding: '4px 12px',
    gap: '4px',
    background: '#FFFFFF',
    color: '#000000DE',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    opacity: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  filterButtonActive: {
    background: colors['keijiban-coral'],
    border: 'none',
    color: 'white',
  },
  chainLogoContainer: {
    width: '36px',
    height: '36px',
    borderRadius: '20px',
    border: '1px solid #00000018',
    backgroundColor: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  chainLogo: {
    maxWidth: '23px',
    maxHeight: '23px',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
  },
}

const FilterBar: React.FC<FilterBarProps> = ({
  filterType,
  isLoadingFilteredPosts,
  bargain_user_info,
  followed_chains = [],
  selectedChain,
  currentPage,
  onFilterTypeChange,
  onChainFilterChange,
}) => {
  const handleFilterClick = (type: 'all' | 'following' | 'want_to_eat' | 'themes') => {
    void trackWithExtra('bargain.message_board.filters.click', { type, from: currentPage })

    // 現在のページと異なるページへの遷移
    if (type === 'themes' && currentPage === 'posts') {
      window.location.href = '/webview/message_board/themes'
      return
    } else if (type !== 'themes' && currentPage === 'themes') {
      // お題一覧から投稿一覧への遷移
      const params = type === 'all' ? '' : `?filter_type=${type}`
      window.location.href = `/webview/message_board/posts${params}`
      return
    }

    onFilterTypeChange(type)
  }

  const handleChainClick = (chainId: string) => {
    void trackWithExtra('bargain.message_board.filters.click', { type: 'chain', from: currentPage })

    if (currentPage === 'themes') {
      // お題一覧からチェーンフィルターでの投稿一覧への遷移
      window.location.href = `/webview/message_board/posts?filter_type=chain&chain_id=${chainId}`
      return
    }

    onChainFilterChange(chainId)
  }

  return (
    <div style={styles.filterButtons}>
      <button
        type="button"
        style={{
          ...styles.filterButton,
          ...(filterType === 'all' ? styles.filterButtonActive : {}),
          ...(isLoadingFilteredPosts ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
        }}
        onClick={() => handleFilterClick('all')}
        disabled={isLoadingFilteredPosts}
      >
        すべて
      </button>
      <button
        type="button"
        style={{
          ...styles.filterButton,
          ...(filterType === 'themes' ? styles.filterButtonActive : {}),
          ...(isLoadingFilteredPosts ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
        }}
        onClick={() => handleFilterClick('themes')}
        disabled={isLoadingFilteredPosts}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            flexShrink: 0,
          }}
        >
          <path
            d="M10.0001 17.5C11.6642 17.4998 13.281 16.9461 14.5959 15.9261C15.9108 14.9062 16.8491 13.4779 17.2632 11.8662C17.6772 10.2544 17.5435 8.5507 16.883 7.02329C16.2225 5.49588 15.0728 4.2315 13.6149 3.42921C12.157 2.62692 10.4736 2.33229 8.8299 2.5917C7.18614 2.8511 5.67535 3.64982 4.53535 4.8621C3.39535 6.07437 2.69091 7.63136 2.53291 9.28794C2.37492 10.9445 2.77235 12.6066 3.66264 14.0125L2.50014 17.5L5.98764 16.3375C7.18729 17.0989 8.57928 17.5022 10.0001 17.5Z"
            stroke={filterType === 'themes' ? '#ffffff' : '#000000DE'}
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        お題一覧
      </button>
      <button
        type="button"
        style={{
          ...styles.filterButton,
          ...(filterType === 'want_to_eat' ? styles.filterButtonActive : {}),
          ...(isLoadingFilteredPosts ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
        }}
        onClick={() => handleFilterClick('want_to_eat')}
        disabled={isLoadingFilteredPosts}
      >
        <span
          className="icon_font icf_heart"
          style={{
            color: filterType === 'want_to_eat' ? '#fff' : colors['keijiban-coral'],
            fontSize: '16px',
          }}
        />
        食べたい！
      </button>
      {bargain_user_info.following_shops_count > 0 && (
        <button
          type="button"
          style={{
            ...styles.filterButton,
            ...(filterType === 'following' ? styles.filterButtonActive : {}),
            ...(isLoadingFilteredPosts ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
          }}
          onClick={() => handleFilterClick('following')}
          disabled={isLoadingFilteredPosts}
        >
          フォロー中
        </button>
      )}
      {followed_chains.map((chain) => (
        <button
          key={chain.id}
          type="button"
          style={{
            ...styles.filterButton,
            ...(chain.logo_url ? { paddingLeft: '1px', paddingRight: '12px' } : {}),
            ...(selectedChain === String(chain.id) ? styles.filterButtonActive : {}),
            ...(isLoadingFilteredPosts ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
          }}
          onClick={() => handleChainClick(String(chain.id))}
          disabled={isLoadingFilteredPosts}
        >
          {chain.logo_url ? (
            <div style={styles.chainLogoContainer}>
              <img src={chain.logo_url} alt={chain.name} style={styles.chainLogo} />
            </div>
          ) : null}
          {chain.name}
        </button>
      ))}
    </div>
  )
}

export default FilterBar
